<template>
    <div  v-if="showConfig" 
            class="absolute bottom-12 right-1/2 transform translate-x-1/2 mt-2 z-50 bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-4 min-w-[320px]"
            @click.stop>
            <div class="flex justify-between items-center mb-4">
                <h3 class="text-white font-medium">Timer Configuration</h3>
                <button  @click="showConfig = false"
                    class="text-gray-400 hover:text-white text-lg leading-none" >
                    ×
                </button>
            </div>

            <div class="mb-4">
                <label class="block text-white text-xs mb-2">Timer Mode:</label>
                <div class="flex gap-2">
                    <button 
                        @click="setMode('countdown')" 
                        :class="[
                            'px-4 py-2 rounded text-xs font-medium transition-colors flex-1',
                            mode === 'countdown' ? 'blue-full-gradient' : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                        ]"
                    >
                        Countdown
                    </button>
                    <button 
                        @click="setMode('countup')" 
                        :class="[
                            'px-4 py-2 rounded text-xs font-medium transition-colors flex-1',
                            mode === 'countup' ? 'green-full-gradient' : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                        ]"
                    >
                        Count Up
                    </button>
                </div>
            </div>

            <div v-if="mode === 'countdown'" class="mb-4">
                <label class="block text-white text-xs mb-2">Countdown Duration:</label>
                <div class="flex gap-2 items-center justify-center mb-3">
                    <div class="flex flex-col items-center">
                        <input 
                            v-model.number="inputHours" 
                            type="number" 
                            min="0" 
                            max="23" 
                            class="w-16 px-2 py-2 text-center bg-gray-700 border border-gray-600 rounded text-white text-lg font-mono"
                            placeholder="00"
                            @blur="applyTimer"
                        >
                        <span class="text-xs text-gray-400 mt-1">Hours</span>
                    </div>
                    <span class="text-2xl text-gray-400">:</span>
                    <div class="flex flex-col items-center">
                        <input 
                            v-model.number="inputMinutes" 
                            type="number" 
                            min="0" 
                            max="59" 
                            class="w-16 px-2 py-2 text-center bg-gray-700 border border-gray-600 rounded text-white text-lg font-mono"
                            placeholder="00"
                            @blur="applyTimer"
                        >
                        <span class="text-xs text-gray-400 mt-1">Minutes</span>
                    </div>
                    <span class="text-2xl text-gray-400">:</span>
                    <div class="flex flex-col items-center">
                        <input 
                            v-model.number="inputSeconds" 
                            type="number" 
                            min="0" 
                            max="59" 
                            class="w-16 px-2 py-2 text-center bg-gray-700 border border-gray-600 rounded text-white text-lg font-mono"
                            placeholder="00"
                            @blur="applyTimer"
                        >
                        <span class="text-xs text-gray-400 mt-1">Seconds</span>
                    </div>
                </div>

                <div>
                    <label class="block text-white text-xs mb-2">Quick Presets:</label>
                    <div class="flex gap-1 flex-wrap">
                        <button 
                            v-for="preset in timePresets" 
                            :key="preset.label"
                            @click="setPreset(preset)"
                            class="px-2 py-1 rounded text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-colors"
                            :class="{'bg-gradient-to-r from-blue-500 to-purple-500': preset.active}"
                        >
                            {{ preset.label }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Count Up Configuration -->
            <div v-if="mode === 'countup'" class="mb-4">
                <div class="text-xs text-gray-300">
                    <p class="mb-2">Count Up mode starts from 00:00:00 and counts upward.</p>
                    <p>Use this mode to track elapsed time for presentations, practice sessions, or any activity duration.</p>
                </div>
            </div>

            <!-- Control Buttons -->
            <div class="flex gap-2">
                <button 
                    @click="resetTimer"
                    class="px-4 py-2 rounded text-xs font-medium danger transition-colors flex-1"
                >
                    Reset
                </button>
            </div>
        </div>
</template>
<script setup> import { ref, defineExpose, defineEmits } from 'vue';

const showConfig = ref(false);
const mode = ref('countup');
const inputHours = ref(0);
const inputMinutes = ref(0);
const inputSeconds = ref(0);
const emits = defineEmits(['setMode', 'setPreset', 'resetTimer']);

const timePresets = ref([
    { label: '30s', hours: 0, minutes: 0, seconds: 30, active: false },
    { label: '1m', hours: 0, minutes: 1, seconds: 0, active: false },
    { label: '2m', hours: 0, minutes: 2, seconds: 0, active: false },
    { label: '5m', hours: 0, minutes: 5, seconds: 0, active: false },
    { label: '10m', hours: 0, minutes: 10, seconds: 0, active: false },
    { label: '15m', hours: 0, minutes: 15, seconds: 0, active: false },
    { label: '30m', hours: 0, minutes: 30, seconds: 0, active: false },
    { label: '1h', hours: 1, minutes: 0, seconds: 0, active: false }
]);

const setMode = (newMode) => {
    mode.value = newMode;
    emits('setMode', newMode);
};

const applyTimer = () => {
    emits('setPreset', {
        hours: inputHours.value,
        minutes: inputMinutes.value,
        seconds: inputSeconds.value
    });
};

const setPreset = (preset) => {
    inputHours.value = preset.hours;
    inputMinutes.value = preset.minutes;
    inputSeconds.value = preset.seconds;
    timePresets.value.forEach(p => p.active = false);
    preset.active = true;
    emits('setPreset', preset);
};

const resetTimer = () => {
    inputHours.value = 0;
    inputMinutes.value = 0;
    inputSeconds.value = 0;
    emits('resetTimer');
};

const resetDefaultConfiguration = () => {
    inputHours.value = 0;
    inputMinutes.value = 0;
    inputSeconds.value = 0;
    timePresets.value.forEach(p => p.active = false);
    mode.value = 'countup';
    emits('setMode', 'countup');
    emits('resetDefaultConfiguration');
};

defineExpose({
    showConfig,
    mode,
    inputHours,
    inputMinutes,
    inputSeconds,
    timePresets,
    resetDefaultConfiguration
});
</script>